import * as THREE from "three/webgpu";
import {SketchEntityKind} from '../../sketch/solver/constraint/relation-compatibility/SketchEntityKinds';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import {SketchPointObject} from './SketchPointObject';
import type {SketchContour} from '../meshProxy/SketchContour';
import type {SketchCollectionObject} from './SketchCollectionObject';
import {AbstractSketchEdgeObject, type AbstractSketchEdgeObjectOptions,} from './AbstractSketchEdgeObject';
import type {SketchSlotObject} from "./SketchSlotObject";
import {isSketchSlotObject} from '../guards/isSketchSlotObject';
import {normalizeAngleDegrees} from "../../tool";
import {SketchPointKind} from './ISketchObject';
import {PointsMaterialFactory} from '../../material/PointsMaterialFactory';
import type {SketchCloneContext} from './SketchCloneContext';
//------ 辅助对象状态 开始------
import {SketchAuxiliaryObjectState} from './SketchAuxiliaryObjectState';
//------ 辅助对象状态 结束------
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../../sketch/config/SketchGeometryToleranceConfig';
import {refitRaycastAccelerationForGeometry} from '../../raycast-acceleration';
import type {SketchEdgeGeometryRefreshOptions} from '../../../types/mesh/sketch/SketchEdgeGeometryRefreshTypes';

/**
 * 判断对象是否为草图直线对象。
 * @param value 待判断的 Three 对象。
 * @returns 当对象满足草图直线协议时返回 true。
 */
/**
 * {@link SketchLineObject} 构造参数。
 * 与抽象基类参数保持一致，通过类型别名暴露。
 */
export interface SketchLineObjectOptions extends AbstractSketchEdgeObjectOptions {
    /** 创建时所属抽象参考面。 */
    referencePlane: ReferencePlane;
    /** 是否为无限长度（默认 false）。 */
    isInfiniteLength?: boolean;
    //------ 中心线语义 开始------
    /** 是否为中心线（默认 false）。 */
    isCenterline?: boolean;
    //------ 中心线语义 结束------
    //------ 绘制预览线标记 开始------
    /** 是否为绘制过程中的临时预览线。 */
    isDrawingPreviewLine?: boolean;
    //------ 绘制预览线标记 结束------
    //------ 辅助对象状态 开始------
    /** 是否为辅助对象。 */
    isAuxiliaryObject?: boolean;
    //------ 辅助对象状态 结束------
    startPoint: SketchPointObject
    endPoint: SketchPointObject
}

/**
 * 草图线段网格对象
 * 职责
 * 1. 使用 THREE.Line 表达一条草图直线边
 * 2. 维护与封闭线段几何（矩形等）的关联关系；
 * 3. 公共几何逻辑（长度、角度、约束求解）均由抽象基类提供
 */
export class SketchLineObject extends AbstractSketchEdgeObject {
    /** 对象运行时类型标记*/
    public readonly objectType = SketchEntityKind.Line;
    isSketchLineObject = true
    /** 所属抽象参考面。 */
    public referencePlane: ReferencePlane;
    /** 起点对象*/
    public startPoint: SketchPointObject;

    /** 终点对象*/
    public endPoint: SketchPointObject;

    /** 默认中点对象。 */
    private readonly midPoint: SketchPointObject;

    //------ 绘制预览线标记 开始------
    /**
     * 是否为绘制过程中的临时预览线。
     * 说明：
     * 1. 该标记仅用于区分“正式草图线”和“绘制态预览线”；
     * 2. 为 true 时，点捕捉与依赖 hoveredObject 的提示逻辑必须忽略该对象；
     * 3. 默认值为 false，只有绘制处理器创建的临时预览线才会显式置为 true。
     */
    public isDrawingPreviewLine: boolean = false;
    //------ 绘制预览线标记 结束------

    //------ 辅助对象状态 开始------
    /** 辅助对象状态内部存储。 */
    private _auxiliaryObjectState: SketchAuxiliaryObjectState = SketchAuxiliaryObjectState.Normal;

    /**
     * 获取当前线对象是否为辅助对象。
     * @returns 若为辅助对象返回 true，否则返回 false。
     */
    public get isAuxiliaryObject(): boolean {
        return this._auxiliaryObjectState === SketchAuxiliaryObjectState.Auxiliary;
    }

    /**
     * 设置当前线对象是否为辅助对象。
     * 说明：
     * 1. 线对象切换辅助状态时，会同步其起点、终点和默认中点；
     * 2. 这样可确保与该线绑定的点对象拥有一致的事件语义。
     * @param value 新的辅助对象标记。
     */
    public set isAuxiliaryObject(value: boolean) {
        this._auxiliaryObjectState = value
            ? SketchAuxiliaryObjectState.Auxiliary
            : SketchAuxiliaryObjectState.Normal;
        this.startPoint.isAuxiliaryObject = value;
        this.endPoint.isAuxiliaryObject = value;
        this.midPoint.isAuxiliaryObject = value;
    }
    //------ 辅助对象状态 结束------

    //------ 中心线语义 开始------
    /**
     * 是否为中心线。
     * 说明：
     * 1. 中心线具有专用尺寸语义（半径/直径式距离、双倍距离、对称角，01 基线 §7.4），
     *    不能等同普通构造线，必须以一等字段区分；
     * 2. 该字段随 cloneSelf 继承（同一逻辑实体），并进入草图持久化协议。
     */
    public isCenterline = false;
    //------ 中心线语义 结束------

    /** 是否属于封闭线段几何*/
    public isClosedGeometryLine = false;

    /** 关联的封闭线段几何对象*/
    public closedCollection: SketchCollectionObject | SketchSlotObject | null = null;

    /** 无限长度标记内部存储。 */
    private isInfiniteLengthValue: boolean = false;

    /** 实例计数，用于生成显示名称*/
    static count = 0;

    public get baseDisplayName(): string {
        return '直线';
    }

    /**
     * 构造草图线对象
     * @param options 构造参数
     */
    public constructor(options: SketchLineObjectOptions) {
        super(options);
        this.referencePlane = options.referencePlane;
        this.startPoint = options.startPoint;
        this.endPoint = options.endPoint;
        //------ 绘制预览线标记 开始------
        // 在构造阶段写入预览线标记，确保外部交互链路可直接读取。
        this.isDrawingPreviewLine = options.isDrawingPreviewLine ?? false;
        //------ 绘制预览线标记 结束------
        //------ 中心线语义 开始------
        this.isCenterline = options.isCenterline ?? false;
        //------ 中心线语义 结束------
        this.startPoint.attachEndpointRole();
        this.endPoint.attachEndpointRole();
        this.isInfiniteLengthValue = options.isInfiniteLength ?? false;
        /**
         * 线段在构造完成前必须先拥有两点几何，
         * 否则外部若提前触发 `updateGeometry` 将拿不到 `position` 属性。
         */
        this.geometry = SketchLineObject.createTwoPointGeometry(this.startPoint, this.endPoint);
        //------ 构造线距离同步收口 开始------
        /**
         * 构造线/中心线 Node 材质依赖 lineDistance/lineDistancePercent/points 节奏输入，
         * 几何就绪后立即同步，保证绘制、克隆、反序列化等所有创建路径都直接具备虚线外观，
         * 不再依赖交互状态机或属性面板的后续刷新“顺手治愈”。
         */
        if (this.shouldSyncLineDistancesForMaterial(this.material)) {
            this.computeLineDistances();
        }
        //------ 构造线距离同步收口 结束------
        this.startPoint.registerConnectedEdge(this);
        this.endPoint.registerConnectedEdge(this);
        this.midPoint = new SketchPointObject({
            position: this.getMidpointPosition(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: this.referencePlane,
            //------ 辅助对象状态 开始------
            isAuxiliaryObject: options.isAuxiliaryObject ?? false,
            //------ 辅助对象状态 结束------
        });
        this.midPoint.attachMidPointRole();
        //------ 线预览点捕捉过滤 开始------
        if (this.isDrawingPreviewLine) {
            // 预览线自带的起点、终点、中点都不应参与点捕捉。
            this.startPoint.isDrawingPreviewPoint = true;
            this.endPoint.isDrawingPreviewPoint = true;
            this.midPoint.isDrawingPreviewPoint = true;
        }
        //------ 线预览点捕捉过滤 结束------
        this.add(this.midPoint);
        this.registerRelatedSketchPoints([this.startPoint, this.endPoint, this.midPoint]);
        //------ 辅助对象状态 开始------
        // 统一回写线对象辅助状态，确保起终点与默认中点保持一致。
        this.isAuxiliaryObject = options.isAuxiliaryObject ?? false;
        //------ 辅助对象状态 结束------
        this.name = `${this.baseDisplayName}${SketchLineObject.count}`;
        SketchLineObject.count += 1;
        this.syncAuxiliaryPoints();
    }

    protected getEdgeStartPoint(): SketchPointObject {
        return this.startPoint;
    }

    protected getEdgeEndPoint(): SketchPointObject {
        return this.endPoint;
    }

    /**
     * 获取是否为无限长度。
     * @returns 当前无限长度标记。
     */
    public get isInfiniteLength(): boolean {
        return this.isInfiniteLengthValue;
    }

    /**
     * 设置是否为无限长度。
     * @param value 新的无限长度标记。
     */
    public set isInfiniteLength(value: boolean) {
        if (this.isInfiniteLengthValue === value) {
            return;
        }
        this.isInfiniteLengthValue = value;
        this.updateGeometry();
    }

    //------ 草图对象拖拽能力判定 开始------
    /**
     * 判断当前直线对象是否允许拖动。
     * 说明：
     * 1. 先检查线对象自身固定约束；
     * 2. 再检查起点与终点是否存在固定约束；
     * 3. 任一条件不满足时，整条线视为不可拖动。
     * @returns 允许拖动返回 true，否则返回 false。
     */
    public override canDrag(): boolean {
        if (!super.canDrag() || this.startPoint.isFixed() || this.endPoint.isFixed()) {
            return false;
        }

        return true;
    }
    //------ 草图对象拖拽能力判定 结束------

    /** 无限延伸距离（与 SketchLineDrawingHandler.resolveLinePoints 保持一致）。 */
    private static readonly INFINITE_EXTEND_LENGTH = 1000;

    /**
     * 原位重写两点线段顶点，刷新包围体，处理虚线材质距离。
     * 说明：
     * 1. 当 isInfiniteLength 为 true 时，沿起点→终点方向向两端各延伸 1000 个单位；
     * 2. 延伸仅影响渲染几何，不修改 SketchPointObject 的逻辑位置。
     * @param options 派生几何刷新选项。
     * @throws 当线段几何缺少 `position` 顶点属性时抛出错误。
     */
    //------ 移除废弃直线刷新别名 开始------
    public updateGeometry(options?: SketchEdgeGeometryRefreshOptions): void {
        const positionAttr = this.geometry.getAttribute('position');
        if (!positionAttr) {
            throw new Error('线段几何缺少 position 属性，无法更新几何。');
        }
        const start = this.startPoint.getPosition();
        const end = this.endPoint.getPosition();

        // 计算实际渲染端点（无限延伸时沿方向拓展）。
        let renderStart = start;
        let renderEnd = end;
        if (this.isInfiniteLength) {
            const direction = end.clone().sub(start);
            // 方向退化时默认沿参考面 X 轴方向。
            if (direction.lengthSq() < SKETCH_GEOMETRY_TOLERANCE_CONFIG.lineDirectionLengthSquared) {
                direction.copy(this.referencePlane.xAxis);
            }
            direction.normalize();
            const extendLength = SketchLineObject.INFINITE_EXTEND_LENGTH;
            renderStart = start.clone().addScaledVector(direction, -extendLength);
            renderEnd = start.clone().addScaledVector(direction, extendLength);
        }

        // 原位重写两点线段顶点。
        positionAttr.setXYZ(0, renderStart.x, renderStart.y, renderStart.z);
        positionAttr.setXYZ(1, renderEnd.x, renderEnd.y, renderEnd.z);
        positionAttr.needsUpdate = true;

        // 几何改变后同步包围体，保证拾取与射线测试正确。
        this.geometry.computeBoundingBox();
        this.geometry.computeBoundingSphere();
        // 同拓扑原位改写顶点后必须刷新既有 BVH，避免拾取结构仍指向旧线段位置。
        refitRaycastAccelerationForGeometry(this.geometry);

        if (this.shouldSyncLineDistancesForMaterial(this.material)) {
            this.computeLineDistances();
        }

        this.syncAuxiliaryPoints();

        // 批量求解时由宿主统一去重刷新，独立改线仍保持即时同步。
        this.requestCompositeHostContourRefresh(this.closedCollection, options);
    }
    //------ 移除废弃直线刷新别名 结束------

    /**
     * 获取默认中点对象。
     * @returns 中点对象。
     */
    public getMidPoint(): SketchPointObject {
        return this.midPoint;
    }

    //------ 草图对象复制 开始------
    /**
     * 复制当前直线对象，返回与源对象完全解耦的新实例。
     * @param context 复制上下文。
     * @returns 当前直线对象的独立副本。
     */
    public cloneSelf(context?: SketchCloneContext): SketchLineObject {
        const clonedLine = new SketchLineObject({
            ...this.createCloneBaseOptions(),
            referencePlane: this.referencePlane,
            isInfiniteLength: this.isInfiniteLength,
            //------ 中心线语义 开始------
            isCenterline: this.isCenterline,
            //------ 中心线语义 结束------
            //------ 绘制预览线标记 开始------
            isDrawingPreviewLine: this.isDrawingPreviewLine,
            //------ 绘制预览线标记 结束------
            //------ 辅助对象状态 开始------
            isAuxiliaryObject: this.isAuxiliaryObject,
            //------ 辅助对象状态 结束------
            startPoint: this.startPoint.cloneSelf(context),
            endPoint: this.endPoint.cloneSelf(context),
        });
        this.registerObjectClone(context, clonedLine);

        // 复制默认中点可见性，保持交互表现一致。
        clonedLine.getMidPoint().visible = this.midPoint.visible;
        clonedLine.getMidPoint().isActiveForSnap = this.midPoint.isActiveForSnap;
        if (context) {
            context.pointMap.set(this.midPoint.uuid, clonedLine.getMidPoint());
            context.objectMap.set(this.midPoint.uuid, clonedLine.getMidPoint());
        }
        // 复制线对象的单目标几何关系（如水平/竖直/固定）。
        this.cloneSingleTargetConstraintsTo(clonedLine, context);
        this.syncCommonDisplayStateToClone(clonedLine);
        return clonedLine;
    }
    //------ 草图对象复制 结束------

    // ─── 线特有方────────────────────────────────────────────────────────────

    /**
     * 获取当前线段角度（度，以参考面 X+ 轴为 0°，逆时针为正，范围 [0, 360)）。
     * @returns 当前角度（度）。
     */
    public get angleDeg(): number {
        const start = this.startPoint.getPosition();
        const end = this.endPoint.getPosition();
        // 将起终点转换为参考面局部 2D 坐标。
        const startLocal = this.referencePlane.worldToLocal(start);
        const endLocal = this.referencePlane.worldToLocal(end);
        const dx = endLocal.x - startLocal.x;
        const dy = endLocal.y - startLocal.y;
        // atan2(dy, dx) 以 X+ 为 0°，逆时针为正方向。
        const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
        return normalizeAngleDegrees(angleDeg);
    }


    /**
     * 获取线段中点位置（世界坐标系）。
     * @returns 线段中点的世界坐标。
     */
    public getMidpointPosition(): THREE.Vector3 {
        // 读取起点世界坐标。
        const start = this.startPoint.getPosition();
        // 读取终点世界坐标。
        const end = this.endPoint.getPosition();
        // 使用向量平均计算中点：mid = (start + end) / 2。
        return start.add(end).multiplyScalar(0.5);
    }

    /**
     * 解析当前草图直线作为镜像轴时的世界几何。
     *
     * 返回独立坐标副本，避免关系编译或预览阶段持有可变控制点状态；零长直线没有
     * 可定义的反射方向，因此命令选择阶段显式拒绝。已建立关系在拖拽中瞬时退化时，
     * 仍由统一参数求解器原子拒绝该帧并保留同一会话供下一帧继续穿越。
     * @returns 当前非退化无限轴线；起终点重合时返回 null。
     */
    public resolveMirrorAxisGeometry(): {startWorld: THREE.Vector3; endWorld: THREE.Vector3} | null {
        const startWorld = this.startPoint.getPosition();
        const endWorld = this.endPoint.getPosition();
        return startWorld.distanceToSquared(endWorld)
            > SKETCH_GEOMETRY_TOLERANCE_CONFIG.lineDirectionLengthSquared
            ? {startWorld, endWorld}
            : null;
    }


    /**
     * 固定起点，按指定长度更新终点
     * @param length 目标长度（场景单位）
     */
    public setLength(length: number): void {
        const start = this.startPoint.getPosition();
        const end = this.endPoint.getPosition();
        const direction = end.clone().sub(start);
        const nextEnd = start.clone().add(direction.normalize().multiplyScalar(length));
        this.endPoint.setPosition(nextEnd);
        this.syncEndpointsToGeometry();
    }

    /**
     * 固定起点，按指定角度更新终点并重建线几何（保持当前长度）。
     * 角度以参考面 X+ 轴为 0°，逆时针为正。
     * @param value 目标角度（度）。
     */
    public set angleDeg(value: number) {
        const start = this.startPoint.getPosition();
        const startLocal = this.referencePlane.worldToLocal(start);
        const length = this.getLength();
        const angleRad = THREE.MathUtils.degToRad(normalizeAngleDegrees(value));
        // X+ 为 0° 时：X 分量 = cos(θ)，Y 分量 = sin(θ)。
        const nextEndLocal = new THREE.Vector2(
            startLocal.x + Math.cos(angleRad) * length,
            startLocal.y + Math.sin(angleRad) * length,
        );
        const nextEnd = this.referencePlane.localToWorld(nextEndLocal);
        this.endPoint.setPosition(nextEnd);
        this.syncEndpointsToGeometry();
    }

    /**
     * 获取当前边长度（参考面局部二维度量）。
     * @returns 当前长度。
     */
    public getLength(): number {
        const start = this.startPoint.getPosition();
        const end = this.endPoint.getPosition();
        const startLocal = this.referencePlane.worldToLocal(start);
        const endLocal = this.referencePlane.worldToLocal(end);
        const dx = endLocal.x - startLocal.x;
        const dy = endLocal.y - startLocal.y;
        return Math.hypot(dx, dy);
    }

    /**
     * 替换起点对象。
     * @param point 新起点对象。
     */
    public setStartPoint(point: SketchPointObject): void {
        this.startPoint.unregisterConnectedEdge(this);
        this.unregisterRelatedSketchPoint(this.startPoint);
        this.startPoint.detachEndpointRole();
        this.startPoint = point;
        //------ 辅助对象状态 开始------
        // 线对象替换端点后，保持端点与线本体的辅助对象语义一致。
        this.startPoint.isAuxiliaryObject = this.isAuxiliaryObject;
        //------ 辅助对象状态 结束------
        this.startPoint.registerConnectedEdge(this);
        this.startPoint.attachEndpointRole();
        this.registerRelatedSketchPoint(this.startPoint);
        this.syncEndpointsToGeometry();
    }

    /**
     * 替换终点对象。
     * @param point 新终点对象。
     */
    public setEndPoint(point: SketchPointObject): void {
        this.endPoint.unregisterConnectedEdge(this);
        this.unregisterRelatedSketchPoint(this.endPoint);
        this.endPoint.detachEndpointRole();
        this.endPoint = point;
        //------ 辅助对象状态 开始------
        // 线对象替换端点后，保持端点与线本体的辅助对象语义一致。
        this.endPoint.isAuxiliaryObject = this.isAuxiliaryObject;
        //------ 辅助对象状态 结束------
        this.endPoint.registerConnectedEdge(this);
        this.endPoint.attachEndpointRole();
        this.registerRelatedSketchPoint(this.endPoint);
        this.syncEndpointsToGeometry();
    }

    /**
     * 同时替换起点与终点对象。
     * @param startPoint 新起点对象。
     * @param endPoint 新终点对象。
     */
    public setEndpoints(startPoint: SketchPointObject, endPoint: SketchPointObject): void {
        this.startPoint.unregisterConnectedEdge(this);
        this.endPoint.unregisterConnectedEdge(this);
        this.unregisterRelatedSketchPoints([this.startPoint, this.endPoint]);
        this.startPoint.detachEndpointRole();
        this.endPoint.detachEndpointRole();
        this.startPoint = startPoint;
        this.endPoint = endPoint;
        //------ 辅助对象状态 开始------
        // 批量替换端点时，同步辅助对象语义，避免端点事件行为漂移。
        this.startPoint.isAuxiliaryObject = this.isAuxiliaryObject;
        this.endPoint.isAuxiliaryObject = this.isAuxiliaryObject;
        //------ 辅助对象状态 结束------
        this.startPoint.registerConnectedEdge(this);
        this.endPoint.registerConnectedEdge(this);
        this.startPoint.attachEndpointRole();
        this.endPoint.attachEndpointRole();
        this.registerRelatedSketchPoints([this.startPoint, this.endPoint]);
        this.syncEndpointsToGeometry();
    }

    /**
     * 解绑两端端点关联，避免残留拓扑引用。
     */
    public detachFromEndpoints(): void {
        this.startPoint.unregisterConnectedEdge(this);
        this.endPoint.unregisterConnectedEdge(this);
        this.unregisterRelatedSketchPoints([this.startPoint, this.endPoint]);
        this.startPoint.detachEndpointRole();
        this.endPoint.detachEndpointRole();
    }

    /**
     * 同步默认辅助点位置。
     */
    private syncAuxiliaryPoints(): void {
        this.midPoint.setPosition(this.getMidpointPosition());
    }

    /**
     * 平移线段（通过平移两个端点实现）
     * @param delta 位移向量
     * @throws 当线段起点或终点被固定时抛出错误。
     */
    public translateLine(delta: THREE.Vector3): void {
        if (this.startPoint.isFixed() || this.endPoint.isFixed()) {
            throw new Error('Line endpoint is fixed and line translation is blocked.');
        }

        const nextStart = this.startPoint.getPosition().add(delta);
        const nextEnd = this.endPoint.getPosition().add(delta);

        // 端点移动后会通过回调自动刷新线几何
        this.startPoint.setPosition(nextStart);
        this.endPoint.setPosition(nextEnd);
    }

    /**
     * 绑定所属封闭线段几何对象
     * @param geometry 目标封闭线段几何对象或槽口对象
     * @throws 当当前线段已绑定其他封闭几何对象时抛出错误。
     */
    public attachClosedCollection(geometry: SketchCollectionObject | SketchSlotObject): void {
        if (this.closedCollection && this.closedCollection !== geometry) {
            throw new Error('当前线已关联其他封闭线段几何对象。');
        }
        this.closedCollection = geometry;
        this.isClosedGeometryLine = true;
        // 通过类型守卫区分：SketchSlotObject 直接取 contour 属性，SketchCollectionObject 调用 getContour()。
        if (isSketchSlotObject(geometry)) {
            this.contour = geometry.contour;
        } else {
            this.contour = geometry.getContour();
        }
    }

    /**
     * 解绑所属封闭线段几何对象
     * @param geometry 若传入，仅在匹配时才解绑
     */
    public detachClosedCollection(geometry?: SketchCollectionObject | SketchSlotObject): void {
        if (geometry && this.closedCollection !== geometry) {
            return;
        }
        this.closedCollection = null;
        this.isClosedGeometryLine = false;
        this.clearContour();
    }


    /**
     * 线段对象是否可创建轮廓。
     * 说明：
     * 1. 单条线段本身不构成轮廓；
     * 2. 仅当线段已被封闭线段几何对象接管时，才可由该几何对象生成轮廓。
     * @returns 若已关联封闭线段几何对象则返回 true。
     */
    public canCreateContour(): boolean {
        return this.closedCollection !== null;
    }

    /**
     * 创建线段所属轮廓。
     * 说明：
     * 1. 实际轮廓由封闭线段几何对象统一生成；
     * 2. 当前方法仅做能力校验与委托，确保职责单一。

     * @returns 创建后的轮廓对象。
     */
    public createContour(): SketchContour {
        return this.createContourFromHost(this.closedCollection, '线段');
    }

}
